import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Loader2, Printer, CheckCircle2, XCircle, Wifi, WifiOff, Clock, Trash2, RefreshCw } from "lucide-react";
import { getPrinters, printReceipt, disconnectQZTray } from "@/lib/qz-tray";
import { useQZTrayConnection } from "@/hooks/useQZTrayConnection";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { getPendingPrintJobs, clearPrintQueue, processPendingPrintJobs, PendingPrintJob } from "@/lib/print-queue";

export function PrinterConnectionTest() {
  const { activeRestaurant } = useRestaurantContext();
  const { 
    isConnected, 
    isConnecting, 
    autoReconnect, 
    connect, 
    toggleAutoReconnect 
  } = useQZTrayConnection();
  
  const [printers, setPrinters] = useState<string[]>([]);
  const [printingPrinter, setPrintingPrinter] = useState<string | null>(null);
  const [pendingJobs, setPendingJobs] = useState<PendingPrintJob[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch pending jobs
  const refreshPendingJobs = () => {
    setPendingJobs(getPendingPrintJobs());
  };

  useEffect(() => {
    refreshPendingJobs();
    const interval = setInterval(refreshPendingJobs, 3000);
    return () => clearInterval(interval);
  }, []);

  // Fetch printers when connected
  useEffect(() => {
    if (isConnected) {
      getPrinters().then(setPrinters);
    } else {
      setPrinters([]);
    }
  }, [isConnected]);

  const handleConnect = async () => {
    const success = await connect();
    if (success) {
      const availablePrinters = await getPrinters();
      setPrinters(availablePrinters);
      toast.success("Conectado ao QZ Tray com sucesso!");
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectQZTray();
      setPrinters([]);
      toast.success("Desconectado do QZ Tray");
    } catch (error) {
      console.error("Failed to disconnect:", error);
    }
  };

  const handleTestPrint = async (printerName: string) => {
    setPrintingPrinter(printerName);
    try {
      const restaurantName = activeRestaurant?.name || "ALTUS FOOD";
      const now = new Date();
      const dateStr = format(now, "dd/MM/yyyy", { locale: ptBR });
      const timeStr = format(now, "HH:mm:ss", { locale: ptBR });

      const testContent = [
        "\x1B\x40", // Initialize printer
        "\x1B\x61\x01", // Center align
        "================================\n",
        `    ${restaurantName.toUpperCase()}\n`,
        "       TESTE DE IMPRESSAO\n",
        "================================\n",
        "\n",
        `Data: ${dateStr}\n`,
        `Hora: ${timeStr}\n`,
        `Impressora: ${printerName}\n`,
        "\n",
        "--------------------------------\n",
        "\n",
        "  [OK] Impressora configurada\n",
        "       com sucesso!\n",
        "\n",
        "  Se voce esta lendo isso,\n",
        "  a configuracao esta OK.\n",
        "\n",
        "--------------------------------\n",
        "\n",
        "================================\n",
        "\n\n\n",
        "\x1D\x56\x00", // Cut paper
      ].join("");

      await printReceipt(printerName, testContent);
      toast.success(`Teste enviado para ${printerName}`);
    } catch (error) {
      console.error("Failed to print test:", error);
      toast.error(`Falha ao imprimir teste em ${printerName}`);
    } finally {
      setPrintingPrinter(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Printer className="h-5 w-5" />
          Testar Conexão
        </CardTitle>
        <CardDescription>
          Conecte ao QZ Tray e teste suas impressoras
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Auto-reconnect Toggle */}
        <div className="flex items-center justify-between p-3 rounded-lg border">
          <div className="space-y-0.5">
            <Label htmlFor="auto-reconnect" className="font-medium">
              Reconexão Automática
            </Label>
            <p className="text-xs text-muted-foreground">
              Mantém a impressora conectada automaticamente
            </p>
          </div>
          <Switch
            id="auto-reconnect"
            checked={autoReconnect}
            onCheckedChange={toggleAutoReconnect}
          />
        </div>

        {/* Connection Status */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
          <div className="flex items-center gap-2">
            {isConnected ? (
              <>
                <Wifi className="h-5 w-5 text-green-500" />
                <span className="font-medium text-green-600 dark:text-green-400">
                  Conectado ao QZ Tray
                </span>
              </>
            ) : isConnecting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="font-medium text-muted-foreground">
                  Conectando...
                </span>
              </>
            ) : (
              <>
                <WifiOff className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium text-muted-foreground">
                  Desconectado
                </span>
              </>
            )}
          </div>
          
          {isConnected ? (
            <Button variant="outline" size="sm" onClick={handleDisconnect}>
              Desconectar
            </Button>
          ) : (
            <Button onClick={handleConnect} disabled={isConnecting}>
              {isConnecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Conectando...
                </>
              ) : (
                "Conectar ao QZ Tray"
              )}
            </Button>
          )}
        </div>

        {/* Detected Printers */}
        {isConnected && printers.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">
              Impressoras detectadas ({printers.length})
            </h4>
            <div className="space-y-2">
              {printers.map((printer) => (
                <div
                  key={printer}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card"
                >
                  <div className="flex items-center gap-2">
                    <Printer className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{printer}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleTestPrint(printer)}
                    disabled={printingPrinter === printer}
                  >
                    {printingPrinter === printer ? (
                      <>
                        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                        Imprimindo...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="mr-2 h-3 w-3" />
                        Imprimir Teste
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No printers message */}
        {isConnected && printers.length === 0 && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400">
            <XCircle className="h-4 w-4" />
            <span className="text-sm">Nenhuma impressora detectada</span>
          </div>
        )}

        {/* Pending Print Jobs */}
        {pendingJobs.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Impressões Pendentes ({pendingJobs.length})
              </h4>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    setIsProcessing(true);
                    await processPendingPrintJobs();
                    refreshPendingJobs();
                    setIsProcessing(false);
                  }}
                  disabled={!isConnected || isProcessing}
                >
                  {isProcessing ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-1 h-3 w-3" />
                  )}
                  Reimprimir
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    clearPrintQueue();
                    refreshPendingJobs();
                    toast.success("Fila de impressão limpa");
                  }}
                >
                  <Trash2 className="mr-1 h-3 w-3" />
                  Limpar
                </Button>
              </div>
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {pendingJobs.map((job) => (
                <div
                  key={job.id}
                  className="flex items-center justify-between p-2 rounded bg-yellow-50 dark:bg-yellow-900/20 text-xs"
                >
                  <div className="flex items-center gap-2">
                    <Printer className="h-3 w-3 text-yellow-600" />
                    <span className="font-medium">
                      {job.orderNumber ? `Pedido #${job.orderNumber}` : job.logType || "Impressão"}
                    </span>
                    <span className="text-muted-foreground">
                      → {job.printerName}
                    </span>
                  </div>
                  <span className="text-muted-foreground">
                    {job.attempts > 0 && `${job.attempts}x tentativas`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Help message when disconnected */}
        {!isConnected && !isConnecting && (
          <p className="text-sm text-muted-foreground">
            {autoReconnect 
              ? "Tentando reconectar automaticamente. Se não funcionar, verifique se o QZ Tray está rodando."
              : "Se não conseguir conectar, verifique o Guia de Instalação acima."}
          </p>
        )}
      </CardContent>
    </Card>
  );
}